import React, { useEffect } from 'react';

import { Button } from '@gn-audio/jss-components';

import { EarResultGraph } from '../components/EarResultGraph';
import { RecommendedProductTile } from '../components/RecommendedProductTile';
import { StepContainer } from '../components/StepContainer';
import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { translations } from '../translations/en';
import { TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';
import { getRecommendedProducts } from '../utils/resultsCalculation';

export const ResultsStep: React.FC = () => {
  const { state, restartTest, retakeAudioTest, sitecoreFields } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();

  useEffect(() => {
    if (state.results) {
      trackHearingTestEvent(HearingTestEventAction.LoadPage, 'Results');
      trackHearingTestEvent(HearingTestEventAction.LeftEarScore, state.results.leftEarAverage);
      trackHearingTestEvent(HearingTestEventAction.RightEarScore, state.results.rightEarAverage);
    }
  }, [state.results, trackHearingTestEvent]);

  if (!state.results) {
    return (
      <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 text-lg text-gray-600">{translations.calculatingResults}</div>
          </div>
        </div>
      </StepContainer>
    );
  }

  const {
    leftEarAverage,
    rightEarAverage,
    overallSeverityLevel,
    leftEarSeverity,
    rightEarSeverity,
  } = state.results;

  const recommendedProducts = getRecommendedProducts(sitecoreFields, overallSeverityLevel);

  const headline =
    overallSeverityLevel === 'good'
      ? translations.resultheadlinegood
      : translations.resultheadlinehearingloss;

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="space-y-8">
        <div className="text-center">
          <div className="bg-accent-50 rounded-lg p-4 text-center">
            <p className="text-gray-700">{translations.donotforgetvolume}</p>
          </div>
          <h1 className="mb-4 text-3xl font-bold text-gray-900">{headline}</h1>
        </div>

        <div className="mx-auto flex max-w-4xl flex-col gap-6 lg:flex-row">
          <EarResultGraph
            earSide="left"
            averageScore={leftEarAverage}
            severity={leftEarSeverity}
            goodMaxScore={sitecoreFields.goodHearingMaxScore.value}
            moderateMaxScore={sitecoreFields.moderateLossMaxScore.value}
          />
          <EarResultGraph
            earSide="right"
            averageScore={rightEarAverage}
            severity={rightEarSeverity}
            goodMaxScore={sitecoreFields.goodHearingMaxScore.value}
            moderateMaxScore={sitecoreFields.moderateLossMaxScore.value}
          />
        </div>
        <p className="text-gray-700">{translations.disclaimerresults}</p>

        {recommendedProducts.length > 0 && (
          <div className="mx-auto max-w-4xl space-y-6">
            <h2 className="text-center text-2xl font-bold text-gray-900">
              {translations.recommendedProducts}
            </h2>
            <div className="space-y-6">
              {recommendedProducts.map((product) => (
                <RecommendedProductTile key={product.id} product={product} />
              ))}
            </div>
          </div>
        )}

        <div className="text-center">
          <Button
            onClick={() => {
              trackHearingTestEvent(HearingTestEventAction.Click, 'Restart Test');
              restartTest();
            }}
            variant="secondary"
            size="m"
            className="w-full xs:w-auto"
          >
            {translations.restarttest}
          </Button>
          <Button
            onClick={() => {
              trackHearingTestEvent(HearingTestEventAction.Click, 'Retake Audio Test');
              retakeAudioTest();
            }}
            variant="secondary"
            size="m"
            className="ml-0 mt-4 w-full xs:ml-4 xs:mt-0 xs:w-auto"
          >
            {translations.retakeaudiotest}
          </Button>
        </div>
      </div>
    </StepContainer>
  );
};

export default ResultsStep;
